// MENU-WIRE-1 — what this account has done, for the menu.
//
// The same shape as useAccountSignals, and deliberately a separate hook: that
// one answers "which widgets belong on Home" over six counts, this one answers
// "which menu items belong on screen" over twenty six. Sharing a query would
// mean the menu waited on Home's counts and Home waited on the menu's.
//
// UNDEFINED IS NOT AN EMPTY ACCOUNT. While the counts are in flight this
// returns undefined and the caller passes {}, which removes NOTHING - the full
// menu, never a smaller one guessed at from an answer that had not arrived.
// Losing a menu item for a second is how a merchant learns not to trust it.

import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useOptionalAuth } from '../providers/AuthProvider'
import type { MenuActivity } from '../lib/menuMeasure'
import { MENU_CATALOGUE } from '../lib/menuCatalogue'
import { EXTRA_SETTINGS_TABLES } from '../lib/settingsWire'
import { sharedRead } from '../lib/sharedRead'

/**
 * Every table the catalogue measures, plus the two the SETTINGS filter needs.
 *
 * SETTINGS-WIRE-1 — one read, two filters. The settings filter wanted five
 * things and this sweep already answered `canvass_pins` and `paychecks`; these
 * two are the whole remainder, and the fifth (its module list) is the section
 * ids the nav hop has already resolved. There was never a second shape to build.
 */
const TABLES: readonly string[] = [
  ...new Set([
    ...MENU_CATALOGUE.map((i) => i.measure?.table).filter((t): t is string => !!t),
    ...EXTRA_SETTINGS_TABLES,
  ]),
]

async function countOne(table: string, subAccountId: string): Promise<[string, number]> {
  try {
    const { count, error } = await supabase
      // The table names come from the catalogue, which the guard holds to the
      // real schema; the generated client types cannot narrow a runtime list.
      .from(table as never)
      .select('id', { count: 'exact', head: true })
      .eq('sub_account_id', subAccountId)
    if (error) return [table, 0]
    return [table, count ?? 0]
  } catch {
    return [table, 0]
  }
}

export async function fetchMenuSignals(subAccountId: string): Promise<MenuActivity> {
  const pairs = await Promise.all(TABLES.map((t) => countOne(t, subAccountId)))
  return Object.fromEntries(pairs)
}

/**
 * NO REACT QUERY HERE, DELIBERATELY.
 *
 * The first version used `useQuery`, and `useNavSections` is read by six
 * surfaces including ones rendered in tests that never had a QueryClient. It
 * turned "the menu tree" into "the menu tree, if a provider happens to be
 * mounted", and the bottom-nav vocabulary test failed with `No QueryClient set`
 * before it reached a single assertion. Caught by mutation's dry run, not by
 * review.
 *
 * `useAccountNavConfig` next door reads its own table with an effect for the
 * same reason. This mirrors it exactly, so adding the measure to the hop adds
 * no new requirement to any surface that already rendered it.
 *
 * `data` is undefined until the counts land, and undefined is NOT an empty
 * account: the caller passes `{}`, which removes nothing.
 */
export function useMenuSignals(): { data: MenuActivity | undefined } {
  // useOptionalAuth, not useAuth, for the same reason useAccountNavConfig
  // next door uses it: this hop is rendered by surfaces that are mounted in
  // tests without an AuthProvider, and requiring one turns "the menu tree"
  // into "the menu tree, if a provider happens to be mounted". Mutation's dry
  // run caught both this and the QueryClient version before either shipped.
  const currentSubAccountId = useOptionalAuth()?.currentSubAccountId ?? null
  const [data, setData] = useState<MenuActivity | undefined>(undefined)
  useEffect(() => {
    if (!currentSubAccountId) {
      setData(undefined)
      return
    }
    let cancelled = false
    void (async () => {
      // THE-HOME-LOADS-FAST-1 — eight surfaces mount this hop at once, and
      // every one of them swept all thirty six tables. They join one sweep now.
      const next = await sharedRead(`menu-signals:${currentSubAccountId}`, () =>
        fetchMenuSignals(currentSubAccountId),
      )
      if (!cancelled) setData(next)
    })()
    return () => {
      cancelled = true
    }
  }, [currentSubAccountId])
  return { data }
}
